// src/oura/onboarding/setupQuestions.ts
//
// The questions about their business, as the merchant's Setup page asks them
// (one setup). They replace the onboarding Google Form, so each one fills the
// same answer the form did and files the same way.
//
// ONE LIST, ON PURPOSE. Changing a question is an edit here: the wording, the
// hint, the choices, whether it has to be answered. The page draws whatever
// this list says, in this order. No React, so each part can be read and
// checked on its own.

import type { SetupAnswerKey, SetupAnswers } from "@service/aeo/onboarding";

/**
 * list:    a few short entries, one per line ("Bangsar", "Mont Kiara").
 * choices: pick any of the options, or add their own.
 * text:    a sentence or two.
 * line:    one short line.
 * person:  a name, and an email beside it where the server asks one (under `${key}Email`).
 */
export type SetupQuestionKind = "list" | "choices" | "text" | "line" | "person";

export interface SetupQuestion {
    key: SetupAnswerKey;
    kind: SetupQuestionKind;
    /** The question, as the merchant reads it. */
    title: string;
    /** One line under it: why we ask, or what a good answer looks like. */
    hint: string;
    /** Has to be answered before the step can be sent. */
    required: boolean;
    /** What the empty input says. For a list, what an entry looks like. */
    placeholder?: string;
    /** The options a `choices` question offers. */
    options?: string[];
}

export const SETUP_QUESTIONS: SetupQuestion[] = [
    {
        key: "services",
        kind: "list",
        title: "What should people find you for?",
        hint: "Your main services or products. These are what we get AI to recommend you for.",
        required: true,
        placeholder: "Add a service, such as Teeth whitening",
    },
    {
        key: "outletAreas",
        kind: "list",
        title: "Where are your outlets?",
        hint: "The area people search in, one per outlet.",
        required: true,
        placeholder: "Add an area, such as Bangsar",
    },
    {
        key: "customerQuestions",
        kind: "list",
        title: "What do customers ask before they buy?",
        hint: "Price, parking, how long it takes. We answer these where AI reads them.",
        required: false,
        placeholder: "Add a question, such as Do you take walk-ins?",
    },
    {
        key: "goals",
        kind: "choices",
        title: "What would a good year look like?",
        hint: "Pick any, or add your own.",
        required: true,
        // The onboarding Google Form's own choices, word for word, so an answer
        // given here reads the same as every client's who answered the form.
        options: [
            "Increase bookings or enquiries",
            "Increase online sales",
            "Increase visits to physical stores or locations",
            "Launch a new product or service",
            "Expand into a new location or market",
            "Increase brand awareness",
            "Improve visibility in Google Search and AI-generated answers",
        ],
        placeholder: "Add your own",
    },
    {
        key: "approver",
        kind: "person",
        title: "Who approves changes before they go live?",
        hint: "We email them anything that needs a yes.",
        required: true,
        placeholder: "Their name",
    },
    {
        key: "plans",
        kind: "text",
        title: "Anything coming up we should know about?",
        hint: "A new outlet, a launch, a promotion.",
        required: false,
        placeholder: "Such as a new outlet opening in March",
    },
    {
        key: "other",
        kind: "text",
        title: "Anything else?",
        hint: "Anything you want us to know or avoid.",
        required: false,
    },
];

/** No approver email: it is asked only where the server sends one (see `SetupAnswers.approverEmail`). */
export function emptyAnswers(): SetupAnswers {
    return { outletAreas: [], services: [], customerQuestions: [], goals: [], plans: "", approver: "", other: "" };
}

/** An email we can send to: something@something.something, no spaces. */
export function isEmailLike(v: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

/** Whether one answer counts as given: a list with an entry, or text with a word in it. The approver needs a name, and an email where it is asked. */
export function isAnswered(answers: SetupAnswers, key: SetupAnswerKey): boolean {
    if (key === "approver") {
        const named = answers.approver.trim() !== "";
        return answers.approverEmail === undefined ? named : named && isEmailLike(answers.approverEmail);
    }
    const v = answers[key];
    return Array.isArray(v) ? v.some((x) => x.trim() !== "") : typeof v === "string" && v.trim() !== "";
}

/** The required questions still unanswered, in page order. */
export function missingAnswers(answers: SetupAnswers): SetupQuestion[] {
    return SETUP_QUESTIONS.filter((q) => q.required && !isAnswered(answers, q.key));
}

/** "4 of 5 answered", over the required questions: the ones that hold the step. */
export function answeredCount(answers: SetupAnswers): { done: number; total: number } {
    const required = SETUP_QUESTIONS.filter((q) => q.required);
    return { done: required.filter((q) => isAnswered(answers, q.key)).length, total: required.length };
}

/**
 * The answers as they are sent: entries trimmed, blanks and repeats dropped
 * (ignoring case), text trimmed. What the merchant typed is otherwise kept as
 * they typed it.
 */
export function cleanAnswers(answers: SetupAnswers): SetupAnswers {
    const list = (items: string[]) => {
        const seen = new Set<string>();
        const out: string[] = [];
        for (const raw of items) {
            const item = raw.trim().replace(/\s+/g, " ");
            const key = item.toLowerCase();
            if (item && !seen.has(key)) {
                seen.add(key);
                out.push(item);
            }
        }
        return out;
    };
    return {
        outletAreas: list(answers.outletAreas),
        services: list(answers.services),
        customerQuestions: list(answers.customerQuestions),
        goals: list(answers.goals),
        plans: answers.plans.trim(),
        approver: answers.approver.trim(),
        ...(answers.approverEmail === undefined ? {} : { approverEmail: answers.approverEmail.trim() }),
        other: answers.other.trim(),
    };
}

/** Entries from typed or pasted text: one per line, spaces tidied, none twice (ignoring case). */
export function addEntries(value: string[], raw: string): string[] {
    const seen = new Set(value.map((v) => v.toLowerCase()));
    const next = [...value];
    for (const line of raw.split(/\r?\n/)) {
        const item = line.trim().replace(/\s+/g, " ");
        if (item && !seen.has(item.toLowerCase())) {
            seen.add(item.toLowerCase());
            next.push(item);
        }
    }
    return next;
}

/**
 * Fill any key an older payload left out, so the page never reads `undefined`
 * as an answer. Except the approver's email: left out means not asked, so it
 * stays out rather than become a required blank.
 */
export function withDefaults(answers: Partial<SetupAnswers> | null | undefined): SetupAnswers {
    const base = emptyAnswers();
    if (!answers) return base;
    return {
        outletAreas: Array.isArray(answers.outletAreas) ? answers.outletAreas : base.outletAreas,
        services: Array.isArray(answers.services) ? answers.services : base.services,
        customerQuestions: Array.isArray(answers.customerQuestions) ? answers.customerQuestions : base.customerQuestions,
        goals: Array.isArray(answers.goals) ? answers.goals : base.goals,
        plans: typeof answers.plans === "string" ? answers.plans : base.plans,
        approver: typeof answers.approver === "string" ? answers.approver : base.approver,
        ...(typeof answers.approverEmail === "string" ? { approverEmail: answers.approverEmail } : {}),
        other: typeof answers.other === "string" ? answers.other : base.other,
    };
}
